import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import type { ConsentDecision } from "@/lib/shopify-privacy";

/* ── Preference categories ── */
const CATEGORIES = [
  {
    key: "necessary" as const,
    label: "Necessary",
    description: "Required for core site functionality, security, cart, and checkout.",
    locked: true,
  },
  {
    key: "analytics" as const,
    label: "Analytics",
    description: "Helps us understand site traffic and improve performance.",
    locked: false,
  },
  {
    key: "marketing" as const,
    label: "Marketing",
    description: "Used to measure campaigns and support advertising or retargeting where permitted.",
    locked: false,
  },
  {
    key: "preferences" as const,
    label: "Preferences",
    description: "Remembers your settings and improves your browsing experience.",
    locked: false,
  },
] as const;

/* ── Preferences dialog ── */
const PreferencesDialog = ({
  onSave,
  onClose,
  defaults,
}: {
  onSave: (d: { analytics: boolean; marketing: boolean; preferences: boolean }) => void;
  onClose: () => void;
  defaults: { analytics: boolean; marketing: boolean; preferences: boolean } | null;
}) => {
  const [local, setLocal] = useState({
    analytics: defaults?.analytics ?? false,
    marketing: defaults?.marketing ?? false,
    preferences: defaults?.preferences ?? false,
  });

  useEffect(() => {
    if (defaults) {
      setLocal({
        analytics: defaults.analytics,
        marketing: defaults.marketing,
        preferences: defaults.preferences,
      });
    }
  }, [defaults]);

  const toggle = (key: "analytics" | "marketing" | "preferences") =>
    setLocal((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      {/* overlay */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-md mx-4 bg-card border border-border max-h-[85vh] overflow-y-auto">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="heading-x-small text-foreground">Privacy Preferences</h2>
          <button
            onClick={onClose}
            className="text-foreground/50 hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* body */}
        <div className="px-5 py-4 space-y-5">
          {CATEGORIES.map((cat) => (
            <div key={cat.key} className="flex items-start gap-3">
              <div className="pt-0.5">
                {cat.locked ? (
                  <div className="w-9 h-5 rounded-full bg-accent/60 flex items-center justify-end px-0.5 cursor-not-allowed">
                    <div className="w-4 h-4 rounded-full bg-accent-foreground" />
                  </div>
                ) : (
                  <button
                    role="switch"
                    aria-checked={local[cat.key as keyof typeof local]}
                    onClick={() => toggle(cat.key as "analytics" | "marketing" | "preferences")}
                    className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                      local[cat.key]
                        ? "bg-accent justify-end"
                        : "bg-muted justify-start"
                    }`}
                  >
                    <div className="w-4 h-4 rounded-full bg-foreground/90 transition-transform" />
                  </button>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="body-small font-medium text-foreground">
                  {cat.label}
                  {cat.locked && (
                    <span className="ml-2 text-foreground/40 text-[10px] tracking-wide uppercase">
                      Always on
                    </span>
                  )}
                </p>
                <p className="body-x-small text-foreground/60 mt-0.5 leading-relaxed">
                  {cat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs text-foreground/50 hover:text-foreground/80 underline transition-colors px-2 py-1.5"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(local)}
            className="text-xs border border-foreground/20 hover:border-foreground/40 text-foreground/80 hover:text-foreground px-4 py-1.5 transition-colors tracking-wide uppercase"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main banner ── */
interface CookieConsentBannerProps {
  consentState: {
    showBanner: boolean;
    showPreferences: boolean;
    consent: ConsentDecision | null;
    acceptAll: () => void;
    rejectNonEssential: () => void;
    savePreferences: (d: ConsentDecision) => void;
    openPreferences: () => void;
    closePreferences: () => void;
  };
}

const CookieConsentBanner = ({ consentState }: CookieConsentBannerProps) => {
  const {
    showBanner,
    showPreferences,
    consent,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  } = consentState;

  return (
    <>
      {/* Preferences dialog (can be opened from banner OR footer) */}
      {showPreferences && (
        <PreferencesDialog
          onSave={savePreferences}
          onClose={closePreferences}
          defaults={
            consent
              ? { analytics: consent.analytics, marketing: consent.marketing, preferences: consent.preferences }
              : null
          }
        />
      )}

      {/* Banner */}
      {showBanner && !showPreferences && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[60] animate-[slideUp_0.4s_ease-out]"
          role="dialog"
          aria-label="Privacy preferences"
        >
          <div className="border-t border-border bg-background/95 backdrop-blur-sm">
            <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
              <p className="text-xs text-foreground/70 leading-relaxed flex-1">
                We use cookies and similar technologies to support essential site functions, understand traffic,
                and, where permitted, measure marketing performance.{" "}
                <Link to="/cookies" className="underline text-foreground/90 hover:text-foreground">
                  Cookie Policy
                </Link>
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={openPreferences}
                  className="text-xs text-foreground/50 hover:text-foreground/80 underline transition-colors px-2 py-1.5"
                >
                  Your Privacy Choices
                </button>
                <button
                  onClick={rejectNonEssential}
                  className="text-xs border border-foreground/20 hover:border-foreground/40 text-foreground/80 hover:text-foreground px-4 py-1.5 transition-colors tracking-wide uppercase"
                >
                  Necessary Only
                </button>
                <button
                  onClick={acceptAll}
                  className="text-xs border border-foreground/20 hover:border-foreground/40 text-foreground/80 hover:text-foreground px-4 py-1.5 transition-colors tracking-wide uppercase"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
